import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { CalendarIcon, TriangleAlert } from "lucide-react";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { cn } from "@/lib/utils";
import { Switch } from "@/components/ui/switch";
import { validateSlotTimes } from "@/lib/serviceOrder";

interface DateOverrideSlotDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: {
    date: string;
    startTime: string;
    endTime: string;
    durationOverrideMinutes?: number | null;
    label?: string;
    isActive: boolean;
  }) => void;
  initialData?: {
    id?: string;
    date?: string;
    startTime?: string;
    endTime?: string;
    durationOverrideMinutes?: number | null;
    label?: string;
    isActive?: boolean;
  };
  defaultDuration?: number;
  closedDates?: string[];
}

export function DateOverrideSlotDialog({
  open,
  onOpenChange,
  onSubmit,
  initialData,
  defaultDuration = 120,
  closedDates = [],
}: DateOverrideSlotDialogProps) {
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(
    initialData?.date ? new Date(initialData.date + 'T00:00:00') : undefined
  );
  const [startTime, setStartTime] = useState(initialData?.startTime || "12:00");
  const [endTime, setEndTime] = useState(initialData?.endTime || "14:00");
  const [useDurationOverride, setUseDurationOverride] = useState(!!initialData?.durationOverrideMinutes);
  const [durationOverride, setDurationOverride] = useState<number>(initialData?.durationOverrideMinutes || 90);
  const [label, setLabel] = useState(initialData?.label || "");
  const [isActive, setIsActive] = useState(initialData?.isActive ?? true);

  useEffect(() => {
    if (open) {
      setSelectedDate(initialData?.date ? new Date(initialData.date + 'T00:00:00') : undefined);
      setStartTime(initialData?.startTime || "12:00");
      setEndTime(initialData?.endTime || "14:00");
      setUseDurationOverride(!!initialData?.durationOverrideMinutes);
      setDurationOverride(initialData?.durationOverrideMinutes || 90);
      setLabel(initialData?.label || "");
      setIsActive(initialData?.isActive ?? true);
    }
  }, [open, initialData]);

  const slotTimeError = validateSlotTimes(startTime, endTime);

  const handleSubmit = () => {
    if (slotTimeError) return;
    if (!selectedDate) return;
    const dateStr = format(selectedDate, 'yyyy-MM-dd');
    onSubmit({
      date: dateStr,
      startTime,
      endTime,
      durationOverrideMinutes: useDurationOverride ? durationOverride : null,
      label: label.trim() || undefined,
      isActive,
    });
    onOpenChange(false);
  };

  const isEditing = !!initialData?.id;
  const selectedDateStr = selectedDate ? format(selectedDate, 'yyyy-MM-dd') : null;
  const overridesClosure = Boolean(selectedDateStr && closedDates.includes(selectedDateStr));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[420px]">
        <DialogHeader>
          <DialogTitle>{isEditing ? "Modifier le créneau spécifique" : "Ajouter un créneau spécifique"}</DialogTitle>
          <DialogDescription>
            Ce créneau remplacera les créneaux habituels pour la date sélectionnée.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Date picker */}
          <div className="space-y-2">
            <Label>Date *</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn("w-full justify-start text-left font-normal", !selectedDate && "text-muted-foreground")}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {selectedDate ? format(selectedDate, 'PPP', { locale: fr }) : "Choisir une date"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={setSelectedDate}
                  locale={fr}
                  disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))}
                />
              </PopoverContent>
            </Popover>
          </div>

          {/* Start / End times */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>Début *</Label>
              <Input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>Fin *</Label>
              <Input type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} />
{slotTimeError ? (
  <p className="text-xs text-destructive">{slotTimeError}</p>
) : endTime && startTime && endTime.substring(0, 5) < startTime.substring(0, 5) ? (
  <p className="text-xs text-muted-foreground">Ce service se termine le lendemain à {endTime.substring(0, 5)}. Les réservations restent rattachées au jour de début.</p>
) : null}
            </div>
          </div>

          {overridesClosure && (
            <div className="flex gap-3 rounded-md border border-border bg-muted/50 p-3 text-sm">
              <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
              <div>
                <p className="font-medium">Une fermeture existe déjà à cette date.</p>
                <p className="mt-1 text-muted-foreground">
                  Ce créneau spécifique remplacera les horaires habituels et pourra rouvrir les réservations.
                </p>
              </div>
            </div>
          )}

          {/* Duration override */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Durée personnalisée</Label>
              <Switch checked={useDurationOverride} onCheckedChange={setUseDurationOverride} />
            </div>
            {useDurationOverride ? (
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  min={15}
                  max={480}
                  step={15}
                  value={durationOverride}
                  onChange={(e) => setDurationOverride(Number(e.target.value))}
                  className="w-24"
                />
                <span className="text-sm text-muted-foreground">minutes</span>
              </div>
            ) : (
              <p className="text-xs text-muted-foreground">Durée par défaut : {defaultDuration} min</p>
            )}
          </div>

          {/* Label */}
          <div className="space-y-2">
            <Label>Note interne (optionnel)</Label>
            <Input
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="Ex: Brunch de Pâques"
            />
          </div>

          {/* Active toggle */}
          {isEditing && (
            <div className="flex items-center justify-between">
              <Label>Actif</Label>
              <Switch checked={isActive} onCheckedChange={setIsActive} />
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
          <Button onClick={handleSubmit} disabled={!selectedDate || !startTime || !endTime || !!slotTimeError}>
            {isEditing ? "Modifier" : "Ajouter"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
